import React from 'react';
import ReactModal from 'react-modal';
import { withRouter } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import cn from 'classnames';
import { first } from 'lodash';
import moment from 'moment';

import IMG_CLOSE from 'assets/images/close-button.svg';
import IMG_LOGO from 'assets/images/Slick-Logo2-0_Live_Positive.svg';
import Permitted from 'components/core/Permitted.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import PayoutReport from 'components/report/payment/PayoutReport.component';
import PERM from 'constants/core/perm.const';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import STAFF_GROUP from 'constants/staff/group.const';
import STAFF_NAME from 'constants/staff/name.const';
import { withTranslation } from 'providers/GILT.provider';
import getObject from 'utils/accessors/getObject.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import {
  fromAnyDateToZuluDayBgn,
  fromAnyDateToZuluDayEnd,
  fromAnyDateToZuluWeekBgn,
  fromAnyDateToZuluWeekEnd,
} from 'utils/converters/time/from.util';
import { formatDayToYearLong, formatYearToDayStandard } from 'utils/date/format.util';
import { nowAsMoment } from 'utils/date/now.util';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import byId from 'utils/predicates/byId.util';

import CashupDateByDay from 'cashup/CashupDateByDay';
import CashupDateByRange from 'cashup/CashupDateByRange';
import CashupHeader from 'cashup/CashupHeader';
import CashupLogsWrapper from 'cashup/CashupLogsWrapper.component';
import CashupStaffPicker from 'cashup/CashupStaffPicker.component';
import CashupTableLeft from 'cashup/CashupTableLeft';
import CashupTableRight from 'cashup/CashupTableRight';
import ComingSoon from 'cashup/components/ComingSoon.component';
import { clearTransactionLog } from 'checkout/actions';
import Email from 'checkout/components/Email';
import PopupModal from 'common/PopupModal';
import PadLock from 'lib/components/PadLock';

import CN from './Cashup.module.scss';

const L = logger('Cashup');

const determineOriginals = (options) => {
  const results = asArray(options?.results);

  const transactionsVoidLinks = results.map((item) => (item.created_from ? item.created_from : ''));
  const transactionsWithIndicators = results.map((item) => ({
    ...item,
    void_created: transactionsVoidLinks.includes(item.id),
  }));

  const originals = transactionsWithIndicators.filter((item) => !item.created_from);
  const copies = transactionsWithIndicators.filter((item) => item.created_from);

  for (const copy of copies) {
    const originalIndex = originals.findIndex(({ id }) => id === copy.created_from);
    originals.splice(originalIndex + 1, 0, copy);
  }

  return originals;
};

class Cashup extends React.Component {
  constructor(props) {
    super(props);
    const today = nowAsMoment();

    this.state = {
      personalStylistName: '',
      descOrderModal: false,
      byRangeModal: false,
      selectedFromDate: today,
      selectedToDate: today,
      errorMessageModal: false,
      viewReceiptModal: false,
      receiptData: {
        clients: [],
        deposits: {
          clientDeposits: [],
          isFetching: false,
          errorMessage: '',
        },
        additionalDiscount: 0,
        additionalDiscountType: 'absolute',
        additionalDiscountReason: '',
        paymentType: 'cash',
        tipAmount: 0,
        otherAmount: 0,
        onlineAmount: 0,
        tipType: 'absolute',
        partCash: 0,
        total: 0,
        balanceToPay: 0,
        cardAmount: 0,
        is_void: false,
        is_no_show_fee: false,
        void_created: false,
        effective_date: null,
        active_card: null,
        depositListData: [],
        vouchers_amount: [],
        vouchers_used: [],
      },
      alertSaved: false,
      openDropdown: false,
      selectedStaff: props.selectedStaff ? props.selectedStaff.staff : STAFF_NAME.all,
      currentSelectedStylistId: null,
      isEmployed: '',
      currentPage: 0,
    };
    this.componentRef = React.createRef();
  }

  componentDidMount() {
    windowScrollToPosition(0, 0);
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { errorMessage, saved } = asObject(nextProps.cashup);

    if (saved) {
      this.showAlert();
    } else {
      this.setState({ errorMessageModal: fromAnyToBool(errorMessage) });
    }
  }

  componentWillUnmount() {
    const { feSetSelectedStaff, feCleanCashupData } = this.props;
    call(`Cashup.feCleanCashupData`, feCleanCashupData);
    call(`Cashup.feSetSelectedStaff`, feSetSelectedStaff, STAFF_NAME.all, []);
  }

  selectDate = (event) => {
    const { selectedStaffGroup } = this.props;

    const group = selectedStaffGroup || STAFF_GROUP.all;
    const options = { ...event, group };
    L.info('selectDate()', 'options:', options);

    if (group === STAFF_GROUP.all || group === STAFF_GROUP.salon) {
      this.reFetchByAll(options);
      this.updateDatesInCashupData(options);
    } else {
      this.reFetchByStylists(options);
    }
    this.clearLog();
  };

  reFetchByAll = ({ from: begin, to: end }) => {
    const { beCashupAndReconcile, fetchDepositData, fetchVouchersSold, salonId } = this.props;

    L.debug('reFetchByAll()', 'begin:', begin, 'end:', end);
    L.unignore('reFetchByAll()', 'beCashupAndReconcile:', beCashupAndReconcile({ begin, end }));
    L.unignore('reFetchByAll()', 'fetchDepositData:', fetchDepositData(salonId, begin, end));
    L.unignore('reFetchByAll()', 'fetchVouchersSold:', fetchVouchersSold(salonId, begin, end));
  };

  reFetchByStylists = ({ from, to, ids, group }) => {
    const FTAG = 'reFetchByStylists()';

    if (group === STAFF_GROUP.all) return void L.warn(FTAG, 'Wrong group:', group);

    const { beCashupBreakdown, selectedStylistIds, selectedStaffGroup } = this.props;

    const options = {
      begin: fromAnyDateToZuluDayBgn(from),
      end: fromAnyDateToZuluDayEnd(to),
      ids: ids || selectedStylistIds,
      group: group || selectedStaffGroup,
    };

    L.debug(FTAG, 'options:', options);
    L.unignore(FTAG, 'beCashupBreakdown:', beCashupBreakdown(options));
  };

  updateDatesInCashupData = (options) =>
    void L.unignore(
      'updateDatesInCashupData()',
      'feUpdateCashupData:',
      this.props.feUpdateCashupData({
        ...getObject('cashup.data', this.props),
        startDate: formatDayToYearLong(options?.from),
        endDate: formatDayToYearLong(options?.to),
      }),
    );

  updateData = (data) => {
    this.props.feUpdateCashupData(data);
    this.setState({ viewReceiptModal: false });
  };

  changePage = (data) => {
    const { beCashupAndReconcile } = this.props;
    const { selectedFromDate: begin, selectedToDate: end } = this.state;
    const pageNumber = data.selected + 1;
    this.setState({ currentPage: data.selected });
    L.unignore('changePage()', 'beCashupAndReconcile:', beCashupAndReconcile({ pageNumber, begin, end }));
  };

  changeOrder = () =>
    void this.setState({ descOrderModal: !this.state.descOrderModal }, () => {
      const { beCashupAndReconcile } = this.props;
      const { selectedFromDate: begin, selectedToDate: end, descOrderModal: desc } = this.state;
      L.unignore('changePage()', 'beCashupAndReconcile:', beCashupAndReconcile({ begin, end, desc }));
    });

  clickStaff = () => void this.setState({ openDropdown: !this.state.openDropdown });
  showReceipt = (data) => void this.setState({ viewReceiptModal: true, receiptData: data });
  closeReceipt = () => void this.setState({ viewReceiptModal: false });
  hideErrorMessage = () => void this.props.hideError();
  voidTransaction = (data) => void this.props.voidAndGetDetails(this.props.salonDetails.id, data);
  closeAlert = () => void this.setState({ alertSaved: false });
  clearLog = () => void this.props.clearTransactionLog();
  clearCashup = () => void this.props.feCleanCashupData();

  showAlert = () => {
    this.setState({ alertSaved: true });

    delayed(DELAY.autohide, () => {
      this.setState({ alertSaved: false });
      this.props.resetSaved();
    });
  };

  // For putting void transactions back into checkout
  checkout = (transactionId, clientId, appointmentId) => {
    const { setCheckoutInitDataFromCashup, salonDetails, clients } = this.props;

    const salonId = salonDetails.id;

    L.unignore(
      'checkout()',
      'setCheckoutInitDataFromCashup:',
      setCheckoutInitDataFromCashup(salonId, transactionId, clients, clientId, appointmentId),
    );

    navFor('Cashup', 'checkout()', this.props).push(linkTo({ pattern: LINK.checkoutDay, params: { salonId } }));
  };

  selectGroup = ({ staffGroup, selectedStylist }) => {
    const group = staffGroup || STAFF_GROUP.all;

    if (STAFF_GROUP.all === group) return { group, ids: this.selectAll(), name: STAFF_NAME.all };
    if (STAFF_GROUP.salon === group) return { group, ids: this.selectBusiness(), name: STAFF_NAME.salon };
    if (STAFF_GROUP.employed === group) return { group, ids: this.selectEmployed(), name: STAFF_NAME.employed };
    if (STAFF_GROUP.self === group) return { group, ids: this.selectSelf(), name: STAFF_NAME.self };

    return { group, ids: this.selectSingle(selectedStylist), name: selectedStylist.user_name || selectedStylist };
  };

  setPersonalStylistName = (name) => {
    this.setState({ personalStylistName: name });
  };

  selectStylist = ({ staffGroup, selectedStylist }) => {
    const { selectedFromDate: from, selectedToDate: to } = this.state;
    const { stylistList, feSetSelectedStaff } = this.props;

    const { name, ids, group } = this.selectGroup({ staffGroup, selectedStylist });
    feSetSelectedStaff({ name, ids, group });

    L.info('selectStylist()', 'group:', group, 'ids:', ids);

    if (group === STAFF_GROUP.all || group === STAFF_GROUP.salon) return void this.reFetchByAll({ from, to });

    if (group === STAFF_GROUP.employed || group === STAFF_GROUP.self) {
      return void this.reFetchByStylists({ from, to, ids, group });
    }

    const stylist = first(stylistList.filter(byId(first(ids))));

    const { id, is_self_employed: isSelf, user_name: selectedStaff } = asObject(stylist);
    const isEmployed = id && !isSelf ? '(employed)' : '(self-employed)';

    this.setState({
      selectedStaffGroup: STAFF_GROUP.single,
      selectedStaff,
      isEmployed,
      currentSelectedStylistId: stylist.id,
    });
    this.reFetchByStylists({ from, to, ids, group });
  };

  selectSingle(stylist) {
    const { stylistList } = this.props;

    return typeof stylist === 'string'
      ? stylistList
          .filter((s) => s.user_name === stylist && s.status !== 'deleted' && !s.serves_as_waitlist)
          .map((s) => s.id)
      : stylistList
          .filter((s) => s.user_name === stylist.user_name && s.status !== 'deleted' && !s.serves_as_waitlist)
          .map((s) => s.id);
  }

  selectSelf() {
    const { stylistList } = this.props;

    this.setState({ selectedStaffGroup: STAFF_GROUP.self, selectedStaff: STAFF_NAME.self, isEmployed: '' });

    return stylistList
      .filter((s) => s.is_self_employed === true && s.status !== 'deleted' && !s.serves_as_waitlist)
      .map((s) => s.id);
  }

  selectEmployed() {
    const { stylistList } = this.props;

    this.setState({ selectedStaffGroup: STAFF_GROUP.employed, selectedStaff: STAFF_NAME.employed, isEmployed: '' });

    return stylistList
      .filter((s) => s.is_self_employed === false && s.status !== 'deleted' && !s.serves_as_waitlist)
      .map((s) => s.id);
  }

  selectAll() {
    const { selectedFromDate: from, selectedToDate: to } = this.state;
    const { feUpdateCashupData } = this.props;

    this.setState({ selectedStaffGroup: STAFF_GROUP.all, selectedStaff: STAFF_NAME.all, isEmployed: '' });

    feUpdateCashupData({
      ...getObject('cashup.data', this.props),
      startDate: formatDayToYearLong(from),
      endDate: formatDayToYearLong(to),
    });

    return [];
  }

  selectBusiness() {
    const { selectedFromDate: from, selectedToDate: to } = this.state;
    const { feUpdateCashupData } = this.props;

    this.setState({ selectedStaffGroup: STAFF_GROUP.salon, selectedStaff: STAFF_NAME.salon, isEmployed: '' });

    feUpdateCashupData({
      ...getObject('cashup.data', this.props),
      startDate: formatDayToYearLong(from),
      endDate: formatDayToYearLong(to),
    });

    return [];
  }

  onDayRangeClickHandler = () => {
    const { byRangeModal } = this.state;

    // FIXME: Fix with better implementation of this check and overall better menu selector

    this.setState({ isEmployed: ' ' });

    const now = nowAsMoment();
    const from = byRangeModal ? now : moment(fromAnyDateToZuluWeekBgn(now));
    const to = byRangeModal ? now : moment(fromAnyDateToZuluWeekEnd(now));

    this.setState({ selectedFromDate: from, selectedToDate: to, byRangeModal: !byRangeModal });
    this.updateDatesInCashupData({ from, to });
    this.clearLog();
  };

  onFromDateChange = (from) => {
    const { selectedToDate: stateTo } = this.state;

    this.clearCashup();
    this.setState({ selectedFromDate: from });
    this.selectDate({ from, to: stateTo });
  };

  onToDateChange = (to) => {
    const { selectedFromDate: stateFrom } = this.state;

    this.clearCashup();
    this.setState({ selectedToDate: to });
    this.selectDate({ from: stateFrom, to });
  };

  onDateChange = (date) => {
    this.setState({ selectedFromDate: date, selectedToDate: date });
    this.selectDate({ from: date, to: date });
  };

  onRangeChange = (from, to) => {
    this.setState({ selectedFromDate: from, selectedToDate: to });
    this.selectDate({ from, to });
  };

  onSave = () => {
    const { selectedFromDate: stateFrom } = this.state;
    const { beSaveCashupReconcile } = this.props;

    const cd = getObject('cashup.data', this.props);

    const data = {
      day: formatYearToDayStandard(stateFrom),
      salonCardReceipts: cd.cashReceiptTotal,
      startCashInTill: cd.cashInTillStart,
      endCashInTill: cd.actualCashInTill,
      pettyCash: cd.pettyCashOut,
      cashBanked: cd.cashTakenToBank,
      notes: cd.notes,
    };

    L.unignore('onSave()', 'beSaveCashupReconcile:', beSaveCashupReconcile({ data }));
  };

  render() {
    const {
      receiptData,
      alertSaved,
      byRangeModal,
      selectedFromDate,
      selectedStaff,
      selectedStaffGroup,
      selectedToDate,
      errorMessageModal,
      isEmployed,
      openDropdown,
      viewReceiptModal,
      personalStylistName,
      currentSelectedStylistId,
    } = this.state;

    const {
      cashup: cashupFromProps,
      salonDetails,
      isBrightMode,
      isDarkMode,
      isLightMode,
      stylistList,
      selectedStylistIds,
      rsBeSaveCashupReconcile,
      permission,
      t,
    } = this.props;

    const { apiData, data, errorMessage, reconcileList, resultStylistId } = asObject(cashupFromProps);

    const group = selectedStaffGroup || STAFF_GROUP.all;
    const isSelfStaff = group === STAFF_GROUP.self;
    const isEmployedStaff = group === STAFF_GROUP.employed;
    const isAllStaff = group === STAFF_GROUP.all || group === STAFF_GROUP.salon;
    const isSalonStaff = group === STAFF_GROUP.salon;
    const isNotAllStaff = !isAllStaff;

    const originals = determineOriginals(apiData);

    const bgnZulu = fromAnyDateToZuluDayBgn(selectedFromDate);
    const endZulu = fromAnyDateToZuluDayEnd(selectedToDate);

    return (
      <div
        data-bem="Cashup"
        data-permission={fromAnyToTrimmed(permission)}
        id="html-for-print-cashup"
        className={cn({
          'cash-up-container': true,
          'light-class': isLightMode,
          'dark-class': isDarkMode,
          'bright-class': isBrightMode,
        })}
        ref={this.componentRef}
      >
        <div data-bem="Cashup__logo" className="cashup-title-for-pdf-print-only">
          <span>{asObject(salonDetails).name}</span>
          <img src={IMG_LOGO} alt="Slick" />
        </div>

        <header data-bem="Cashup__header" className="settings-header stock-global-nav">
          <SidebarMenu />
          <div className="global-nav">
            <div className="page-name text-mobile cashup" />
            <nav className="menu-list">
              <ul className="ul-scroll-menu">
                <CashupHeader
                  byRangeModal={byRangeModal ? 'by-range' : 'by-day'}
                  onDayRangeClick={this.onDayRangeClickHandler}
                />
              </ul>
            </nav>
          </div>
          <PadLock />
        </header>

        <div data-bem="Cashup__modals">
          {viewReceiptModal && (
            <ReactModal
              isOpen
              closeTimeoutMS={0}
              contentLabel="Receipt"
              ariaHideApp
              onRequestClose={this.closeReceipt}
              shouldCloseOnOverlayClick
              role="dialog"
              style={{ overlay: { zIndex: 500 } }}
            >
              <Email data={receiptData} salonDetails={salonDetails} clients={receiptData.clients} />
            </ReactModal>
          )}
          {errorMessageModal && (
            <PopupModal
              popupTitle={t('common.error')}
              popupMessage={errorMessage}
              buttonText={t('common.buttons.close')}
              buttonCallback={this.hideErrorMessage}
            />
          )}
        </div>

        <div data-bem="Cashup__body" className={byRangeModal ? 'by-range' : 'by-day'}>
          <div data-bem="Cashup__wrap" className="cashup-wrap">
            <div data-bem="Cashup__layout-container" className="cashup-layout-container">
              {isNotAllStaff && <strong className="filter-in-use-message">{t('cashUp.noteMessage')}</strong>}

              <div data-bem="Cashup__combine-layout" className="cashup-combine-layout">
                <div className="on-left">
                  <CashupTableLeft
                    isSelfEmployedStaff={cn({
                      'hide-elm-for-self-employed-and-employed': isSelfStaff || isEmployedStaff,
                    })}
                    isStylist={
                      isAllStaff || isSelfStaff || isEmployedStaff || isSalonStaff
                        ? 'some-class'
                        : 'hide-elm-for-stylist'
                    }
                    singleStylistData={isNotAllStaff}
                    cashupData={data}
                    updateData={this.updateData}
                    reconcileList={reconcileList}
                    byRangeModal={byRangeModal ? 'by-range' : 'by-day'}
                  />
                </div>

                <div className="on-right">
                  {(isAllStaff || isSalonStaff) && (
                    <Permitted tag="Cashup__table-right" value={PERM.canViewDailyCashupAllStaff}>
                      <CashupTableRight
                        cashupData={data}
                        updateData={this.updateData}
                        reconcileList={reconcileList}
                        byRangeModal={byRangeModal ? 'by-range' : 'by-day'}
                      />
                    </Permitted>
                  )}

                  <PayoutReport
                    begin={bgnZulu}
                    end={endZulu}
                    ids={selectedStylistIds}
                    group={group}
                    permission={permission}
                    personalStylistName={personalStylistName}
                    selectedStylistId={currentSelectedStylistId}
                  />
                  {/* !!! Notes section is hidden for date-range view with css */}

                  {(isAllStaff || isSalonStaff) && (
                    <Permitted tag="Cashup__note-section" value={PERM.canViewDailyCashupAllStaff}>
                      <div data-bem="Cashup__note-section" className="csh-table-wrap clearfix note-section">
                        {byRangeModal ? null : (
                          <div className="cash-up-note">
                            <strong className="label-name">{t('cashUp.note')}</strong>
                            <textarea
                              className="note"
                              placeholder={t('cashUp.textareaPlaceholder')}
                              value={asObject(data).notes}
                              onChange={(ev) => void this.updateData({ ...data, notes: fromEventToValue(ev) })}
                            />

                            <ComingSoon />
                          </div>
                        )}
                      </div>
                    </Permitted>
                  )}
                </div>
              </div>

              <div data-bem="Cashup__sticky-content" className="sticky-content" style={{ zIndex: '1' }}>
                <ReactToPrint
                  trigger={() => <button className="btn-print-pdf-cash-up">{t('cashUp.printPdf')}</button>}
                  content={() => this.componentRef.current}
                />

                <CashupLogsWrapper
                  originals={originals}
                  onChangePage={this.changePage}
                  currentPage={this.state.currentPage}
                  onChangeOrder={this.changeOrder}
                  onViewReceipt={this.showReceipt}
                  onVoidTransaction={this.voidTransaction}
                  onCheckout={this.checkout}
                  selectedFromDate={selectedFromDate}
                  selectedToDate={selectedToDate}
                  byRangeModal={byRangeModal}
                  selectedStaffGroup={selectedStaffGroup}
                  selectedStylistIds={selectedStylistIds}
                  clearTransactionLog={clearTransactionLog}
                />
              </div>

              {alertSaved && (
                <div className="alert centered success animated fadeInDown" style={{ marginRight: 0 }}>
                  {t('cashUp.successfullySavedAlert')}
                  <span className="close" onClick={this.closeAlert} role="button" tabIndex={0}>
                    <img src={IMG_CLOSE} alt={t('cashUp.closeModal')} />
                  </span>
                </div>
              )}
            </div>

            <Permitted value={PERM.canViewDailyCashupAllStaff}>
              <CashupStaffPicker
                isDropdownOpen={openDropdown}
                isEmployed={isEmployed}
                isNotAllStaff={isNotAllStaff}
                onClick={this.clickStaff}
                onSelect={this.selectStylist}
                stylists={stylistList}
                value={selectedStaff}
              />
            </Permitted>

            <Permitted value={PERM.canViewDailyCashupPersonal}>
              <CashupStaffPicker
                disabled
                isEmployed={isEmployed}
                isNotAllStaff
                setPersonalStylistName={this.setPersonalStylistName}
                stylists={stylistList}
              />
            </Permitted>

            <div data-bem="Cashup__date-picker" className="pickers">
              <h1>{resultStylistId && resultStylistId.totalTip}</h1>
              {byRangeModal ? (
                <CashupDateByRange
                  date={selectedFromDate}
                  onFromDateChange={this.onFromDateChange}
                  onToDateChange={this.onToDateChange}
                  onRangeChange={this.onRangeChange}
                />
              ) : (
                <CashupDateByDay date={selectedFromDate} onDateChange={this.onDateChange} />
              )}
            </div>

            {isAllStaff && (
              <div
                data-bem="Cashup__footer"
                className={cn({
                  'csh-footer': true,
                  [CN.sticky]: true,
                })}
              >
                <div className="csh-ftr-wrap">
                  {/* !!! Save button is hidden for date-range view with css */}
                  <button
                    className={cn({
                      'btn btn-violet': true,
                      disabled: rsBeSaveCashupReconcile,
                    })}
                    onClick={this.onSave}
                  >
                    {t('common.buttons.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// this extra export is needed for tests that provide their own mock for connect()
export { Cashup as Cashup4Test };

const CashupHoc = withRouter(withTranslation()(Cashup));

export default CashupHoc;
